/**
 * offerSlot.js readiness, for the PE sector allowlist.
 *
 * flights-pwa loads the offer config globally and exposes `ui.offerConfigLoaded` in
 * Redux; desktop has no such signal — `HomeHeader` injects the script (so Home and SRP
 * get it as a side effect) and the itinerary page never loads it at all. The sector
 * allowlist would therefore stay `unknown` for the whole itinerary flow, silently
 * dropping a user who was allowlisted on SRP back to the control experience.
 *
 * This module makes the dependency explicit: it guarantees `window.offerJson` is being
 * fetched on any page that asks, and lets React re-read the allowlist once it lands.
 */
import { isServer } from '@/utils/general'
import { getScript } from '@/utils/general/browserHelper'
import { getCleartripScriptOrigin } from '@/utils/general/cleartripOrigin'

/** Matches the script `HomeHeader` / `HomeMain` inject, so we never fetch it twice. */
const OFFER_SLOT_SCRIPT_PATTERN = /offerJson\/(staging|prod)\/offerSlot\.js/

const POLL_INTERVAL_MS = 100
/** offerSlot.js is a static CDN asset; past this it is not coming. */
const POLL_TIMEOUT_MS = 15000

const listeners = new Set<() => void>()

let loaded = false
let started = false

const hasOfferJson = (): boolean => !isServer() && Array.isArray((window as any).offerJson)

const markLoaded = () => {
    if (loaded) return
    loaded = true
    listeners.forEach(listener => listener())
}

const buildOfferSlotUrl = (): string => {
    const origin = getCleartripScriptOrigin()
    const env = ['staging', 'development'].includes(process.env.NEXT_PUBLIC_CT_ENV || '')
        ? 'staging'
        : 'prod'
    return `${origin}/offermgmt/offermgmt/offerJson/${env}/offerSlot.js?${Date.now()}`
}

const isOfferSlotScriptPresent = (): boolean =>
    Array.from(document.getElementsByTagName('script')).some(script =>
        OFFER_SLOT_SCRIPT_PATTERN.test(script.src || ''),
    )

/**
 * Idempotent. Polls rather than relying on the script's own `load` event, because on
 * Home and SRP the script is injected by a component we don't control and may already
 * be in flight (or done) by the time a PE surface mounts.
 */
export const ensureOfferConfigLoaded = (): void => {
    if (isServer() || loaded) return
    if (hasOfferJson()) {
        markLoaded()
        return
    }
    if (started) return
    started = true

    if (!isOfferSlotScriptPresent()) {
        getScript(buildOfferSlotUrl(), () => {})
    }

    const startedAt = Date.now()
    const interval = window.setInterval(() => {
        if (hasOfferJson()) {
            window.clearInterval(interval)
            markLoaded()
            return
        }
        if (Date.now() - startedAt >= POLL_TIMEOUT_MS) {
            window.clearInterval(interval)
        }
    }, POLL_INTERVAL_MS)
}

export const isOfferConfigLoaded = (): boolean => loaded || hasOfferJson()

/** `useSyncExternalStore` subscribe — fires once, when the config lands. */
export const subscribeToOfferConfig = (onChange: () => void): (() => void) => {
    listeners.add(onChange)
    return () => {
        listeners.delete(onChange)
    }
}
